/**
 * Test model shared by web (no-code builder), api (validation) and runner (code generation).
 *
 * Non-technical users author tests as an ordered list of plain-English steps.
 * The runner compiles them into tester-army/e2e test files:
 *   open    -> await app.open(value)          (value = path/URL for web, ignored for mobile)
 *   act     -> await agent.act(value)          ("click Sign in", "type demo@1qa.io in Email")
 *   assert  -> await agent.assert(value)       ("the dashboard greets the user")
 *   wait    -> await new Promise(r => setTimeout(r, Number(value)))  (milliseconds, max 30000)
 */
import { z } from 'zod';
import type { StepDataNote } from './variables.js';

/** Testing methods. MVP ships FUNCTIONAL; others are reserved for the roadmap (engines plug in). */
export const TEST_TYPES = ['FUNCTIONAL', 'SECURITY', 'PERFORMANCE', 'ACCESSIBILITY'] as const;
export type TestType = (typeof TEST_TYPES)[number];
export const ENABLED_TEST_TYPES: readonly TestType[] = ['FUNCTIONAL'];

/**
 * WEB / ANDROID / IOS: functional test automation. PERFORMANCE: performance testing projects (load plans,
 * scripts). SECURITY: security testing projects (scan configs + scope, run by the AI pentester).
 */
export const PLATFORMS = ['WEB', 'ANDROID', 'IOS', 'PERFORMANCE', 'SECURITY'] as const;
export type Platform = (typeof PLATFORMS)[number];

export const BROWSERS = ['chromium', 'firefox', 'webkit'] as const;
export type Browser = (typeof BROWSERS)[number];

export const STEP_KINDS = ['open', 'act', 'assert', 'wait'] as const;
export type StepKind = (typeof STEP_KINDS)[number];

export const stepSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(STEP_KINDS),
  value: z.string().max(2000),
});
export type TestStep = z.infer<typeof stepSchema>;

export const stepsSchema = z.array(stepSchema).min(1).max(200);

export const RUN_STATUSES = ['QUEUED', 'RUNNING', 'PASSED', 'FAILED', 'ERROR', 'CANCELLED'] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];
export const TERMINAL_RUN_STATUSES: readonly RunStatus[] = ['PASSED', 'FAILED', 'ERROR', 'CANCELLED'];

export const RESULT_STATUSES = ['PASSED', 'FAILED', 'ERROR', 'SKIPPED'] as const;
export type ResultStatus = (typeof RESULT_STATUSES)[number];

/** Per-step status: PENDING/RUNNING only while a test executes (TestRun.progress). */
export const STEP_STATUSES = ['PENDING', 'RUNNING', 'PASSED', 'FAILED', 'ERROR', 'SKIPPED'] as const;
export type StepStatus = (typeof STEP_STATUSES)[number];

/** Outcome of one step (TestResult.steps). Step text is snapshotted: the test may be edited later. */
export interface StepResult {
  kind: StepKind;
  value: string;
  status: StepStatus;
  durationMs?: number;
  /** How often the step was retried in place before it passed or finally failed (absent = 0). */
  retries?: number;
  /** Test data the run's collection did not provide: taken from the default test data or written in the test. */
  data?: StepDataNote[];
  /** Output/log for this step on its own (security checks: the tool's output). Masked of secrets. */
  log?: string;
  /** Optional heading this step is grouped under in the report (security audit: the check's category). */
  group?: string;
}

/** The test executing right now (TestRun.progress, null when none), for step-by-step status. */
export interface RunProgress {
  testCaseId: string;
  name: string;
  /** 1-based position in the run. */
  index: number;
  total: number;
  steps: StepResult[];
  /** Parallel runs: every test running right now (this object describes the first of them). */
  running?: { testCaseId: string; name: string; index: number }[];
}

/** BullMQ queue contract between api (producer) and runner (consumer). */
export const RUN_QUEUE_NAME = 'test-runs';
/** A test run (jobs queued before flows existed have no `kind`), or a flow run to orchestrate. */
export type RunJobData = { kind?: 'run'; runId: string } | { kind: 'flow'; flowRunId: string };

/** Ready-made step templates shown in the builder for non-technical users. */
export const STEP_TEMPLATES: { label: string; kind: StepKind; value: string }[] = [
  { label: 'Open a page', kind: 'open', value: '/' },
  { label: 'Click something', kind: 'act', value: 'click the "Sign in" button' },
  { label: 'Type into a field', kind: 'act', value: 'type "demo@1qa.io" into the Email field' },
  { label: 'Choose an option', kind: 'act', value: 'select "Germany" in the Country dropdown' },
  { label: 'Check the page', kind: 'assert', value: 'the page shows a welcome message' },
  { label: 'Wait', kind: 'wait', value: '1000' },
];

/**
 * Test names are unique within a project, ignoring case and surrounding/repeated spaces:
 * "Login" and " login " are the same name. Stored as TestCase.nameKey (unique per project).
 */
export function testNameKey(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** First free "<base>", "<base> (2)", "<base> (3)"… among `taken` names (max 200 characters). */
export function uniqueTestName(base: string, taken: Iterable<string>): string {
  const used = new Set([...taken].map(testNameKey));
  const clean = base.trim().replace(/\s+/g, ' ');
  if (!used.has(testNameKey(clean))) return clean.slice(0, 200);
  for (let n = 2; ; n++) {
    const suffix = ` (${n})`;
    const candidate = clean.slice(0, 200 - suffix.length) + suffix;
    if (!used.has(testNameKey(candidate))) return candidate;
  }
}
